import { getCustomProps } from '../forms'
import { getData } from '../get-table'
import { HttpService } from '../http.service'

const http = new HttpService()

export interface LicenseTypesConcepts {
  masterConceptId: string
  maxDays: string
  licenseTypeId?: string
  action: string
  customFields: any
}

export interface LicenseType {
  companies: string[]
  payrolls: string[]
  name: string
  code: string
  entityId: string
  workingDays: string[]
  holidaysGroupId: string
  pregnancy: boolean
  allowPrimaryAttachment: boolean
  obligatoryPrimaryAttachment: boolean
  enableContinuity: boolean
  continuityDays: string
  appliesSalaryReduction: boolean
  licenseTypesConcepts: LicenseTypesConcepts[]
}

export async function getLicenseTypes(
  offset: number,
  perPage: number,
  dataOrder: string,
  search: string = ''
): Promise<any> {
  // where's
  const where = [
    'entities.country_id=:countryId',
    "((license_types.companies isnull) OR (license_types.companies = '[]') OR (r.company::uuid=:companyId))"
  ]
  if (search) {
    where.push(
      `CONCAT(license_types.code,license_types.name,entities.name) ILIKE '%${search}%'`
    )
  }

  // order by
  let modifier = []
  dataOrder !== ''
    ? dataOrder[1] === 'code'
      ? (modifier = [
          `ORDER BY (CASE WHEN license_types.code ~ '^[0-9]+$' THEN CAST(license_types.code AS NUMERIC) ELSE NULL END) ${dataOrder[0]}, license_types.code ${dataOrder[0]}`
        ])
      : (modifier = [`order by ${dataOrder[1]} ${dataOrder[0]}`])
    : (modifier = [
        "ORDER BY (CASE WHEN license_types.code ~ '^[0-9]+$' THEN CAST(license_types.code AS NUMERIC) ELSE NULL END), license_types.code"
      ])
  // result
  const response: any = await getData({
    tables: ['pg_license_types'],
    fields: [
      'license_types.id',
      'license_types.name',
      'license_types.code',
      'license_types.is_inactive',
      'entities.name as entity',
      'license_types.updated_at',
      'license_types.companies'
    ],
    join: [
      'INNER JOIN entities ON license_types.entity_id=entities.id',
      'LEFT JOIN LATERAL jsonb_array_elements_text(license_types.companies) r(company) ON TRUE',
      'LEFT JOIN companies ON companies.id = r.company::uuid'
    ],
    where,
    modifier,
    options: {
      outputFormat: 'json',
      pagination: { offset, limit: perPage }
    }
  })
  return response[0]
}

export async function getLicenseType(id: string): Promise<LicenseType> {
  const data = await getData<any>({
    tables: ['pg_license_types'],
    fields: [
      'license_types.id',
      'license_types.companies',
      'license_types.payrolls',
      'license_types.name',
      'license_types.code',
      'license_types.entity_id as entityId',
      'license_types.working_days as workingDays',
      'license_types.holidays_group_id as holidaysGroupId',
      'license_types.pregnancy',
      'license_types.allow_primary_attachment as allowPrimaryAttachment',
      'license_types.obligatory_primary_attachment as obligatoryPrimaryAttachment',
      'license_types.enable_continuity as enableContinuity',
      'license_types.continuity_days::text as continuityDays',
      'license_types.applies_salary_reduction as appliesSalaryReduction',
      'license_types.custom_fields as custom'
    ],
    where: [`license_types.id = '${id}'`],
    options: {
      outputFormat: 'json',
      pagination: { offset: 0, limit: 10 }
    }
  })
  return data[0].results[0]
}

export async function createLicenseType(data: LicenseType): Promise<boolean> {
  for (const licenseTypeConcept of data.licenseTypesConcepts) {
    licenseTypeConcept.customFields = {
      ...getCustomProps(licenseTypeConcept)
    }
  }
  const response: any = await http.post('license/license_types', {
    companies: data.companies,
    payrolls: data.payrolls,
    name: data.name,
    code: data.code,
    entityId: data.entityId,
    workingDays: data.workingDays,
    holidaysGroupId: data.holidaysGroupId,
    pregnancy: data.pregnancy,
    allowPrimaryAttachment: data.allowPrimaryAttachment,
    obligatoryPrimaryAttachment: data.obligatoryPrimaryAttachment,
    enableContinuity: data.enableContinuity,
    continuityDays: data.continuityDays,
    appliesSalaryReduction: data.appliesSalaryReduction,
    licenseTypesConcepts: data.licenseTypesConcepts,
    customFields: {
      ...getCustomProps(data)
    }
  })

  if (response?.data?.data?.error) {
    return false
  }
  return true
}

export async function updateLicenseType(
  data: LicenseType,
  id: string
): Promise<boolean> {
  for (const licenseTypeConcept of data.licenseTypesConcepts) {
    delete licenseTypeConcept.licenseTypeId
    licenseTypeConcept.customFields = {
      ...getCustomProps(licenseTypeConcept)
    }
  }
  const response: any = await http.put('license/license_types/' + id, {
    companies: data.companies,
    payrolls: data.payrolls,
    name: data.name,
    code: data.code,
    entityId: data.entityId,
    workingDays: data.workingDays,
    holidaysGroupId: data.holidaysGroupId,
    pregnancy: data.pregnancy,
    allowPrimaryAttachment: data.allowPrimaryAttachment,
    obligatoryPrimaryAttachment: data.obligatoryPrimaryAttachment,
    enableContinuity: data.enableContinuity,
    continuityDays: data.continuityDays,
    appliesSalaryReduction: data.appliesSalaryReduction,
    licenseTypesConcepts: data.licenseTypesConcepts,
    customFields: {
      ...getCustomProps(data)
    }
  })

  if (response?.data?.data?.error) {
    return false
  }
  return true
}

export async function deleteLicenseType(id: string): Promise<boolean> {
  const response: any = await http.delete('license/license_types/' + id)

  if (response?.data?.data?.error) {
    return false
  }
  return true
}

export async function stateLicenseType(
  id: string,
  enable: boolean
): Promise<boolean> {
  const response: any = await http.put(`license/license_types/${id}/enable`, {
    enable
  })

  if (response?.data?.data?.error) {
    return false
  }
  return true
}
